import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, DataList, Dialog, SelectField, TextField, useToast } from '@rooming/ui';
import { isProblem } from '@rooming/web-core';
import { useState } from 'react';
import { backoffice, type Member } from '../api';
import { Loaded, messageOf, SEX_TEXT } from '../common';

// Emergency actions of a guide, on site (owner, 2026-10-27): register a passenger who is standing there and restore a
// passenger's access. Only for the group the guide coordinates; the API refuses (403) anything else.

const DOCUMENT_TYPES = [
  { value: 'RUT', label: 'RUT (Chile)' },
  { value: 'CI_PY', label: 'Cédula (Paraguay)' },
  { value: 'CI_UY', label: 'Cédula (Uruguay)' },
  { value: 'PASSPORT', label: 'Pasaporte' },
  { value: 'OTHER', label: 'Otro' },
];
const DEFAULT_REASON = 'Emergencia en terreno';
const ONBOARDING =
  'Su primera contraseña es su número de documento. Al ingresar, el sistema le pide elegir una clave nueva de al menos 8 caracteres.';

/** The server's field messages (e.g. "Este documento ya se encuentra registrado en el sistema.") read better alone. */
function dialogMessage(error: unknown): string {
  if (isProblem(error) && error.errors.length > 0) return error.errors.map((field) => field.detail).join(' ');
  return messageOf(error);
}

/** Documents are typed with dots, dashes and spaces; only the characters that count reach the server. */
const sanitizeDocument = (value: string): string => value.replace(/[\s.\-_/]/g, '').toUpperCase();

export function GuidePassengers({
  groupId,
  defaultDocumentType,
}: {
  readonly groupId: string;
  readonly defaultDocumentType: string;
}) {
  const members = useQuery({ queryKey: ['guide-members', groupId], queryFn: () => backoffice.guideMembers(groupId) });
  // DR-407: the guide's emergency actions last as long as the group link is open (same query as the invitation panel).
  const invitation = useQuery({
    queryKey: ['activation-invitation', groupId],
    queryFn: () => backoffice.activationInvitation(groupId),
  });
  const linkClosed = invitation.data !== undefined && !invitation.data.open;
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState<Member>();
  const [notice, setNotice] = useState<{ title: string; body: string }>();
  const refresh = useQueryClient();
  const toast = useToast();
  const onDone = (title: string, body: string): void => {
    setNotice({ title, body });
    toast('success', title);
    void refresh.invalidateQueries({ queryKey: ['guide-members', groupId] });
  };
  return (
    <section aria-labelledby="guide-passengers-title" className="mt-8 flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="guide-passengers-title" className="text-xl font-semibold">
          Pasajeros del grupo
        </h2>
        {!linkClosed && (
          <Button
            onClick={() => {
              setNotice(undefined);
              setAdding(true);
            }}
          >
            + Agregar Pasajero
          </Button>
        )}
      </div>
      {linkClosed ? (
        <Alert tone="warning" title="El enlace del grupo está cerrado">
          Desde ahora solo un administrador de Funtour puede agregar personas o restablecer accesos. Avisale si hace
          falta.
        </Alert>
      ) : (
        <Alert tone="info">
          Para emergencias en terreno, mientras el enlace del grupo esté abierto: si un pasajero no puede ingresar o
          falta en la lista, podés registrarlo o restablecer su acceso. Todo queda registrado con tu nombre.
        </Alert>
      )}
      {notice !== undefined && (
        <Alert tone="success" title={notice.title}>
          {notice.body}
        </Alert>
      )}
      <Loaded query={members}>
        {(items) => (
          <DataList<Member>
            caption="Pasajeros del grupo"
            rows={items.filter((m) => m.status === 'ACTIVE')}
            rowKey={(m) => m.id}
            empty="Todavía no hay pasajeros en este grupo."
            columns={[
              {
                header: 'Nombre',
                // The state travels with the name (DR-408): Pendiente de activar = hay que pasarle el enlace.
                cell: (m) => (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    {`${m.lastNames}, ${m.firstNames}`}
                    {m.accountState === 'PENDING' && <Badge tone="warning">Pendiente de activar</Badge>}
                    {m.accountState === 'ACTIVATED' && <Badge tone="success">Activo</Badge>}
                  </span>
                ),
              },
              {
                header: 'Rol',
                cell: (m) =>
                  m.role === 'PASSENGER' ? (
                    'Pasajero'
                  ) : m.role === 'COMPANION' ? (
                    'Acompañante'
                  ) : (
                    <Badge tone="info">Apoderado</Badge>
                  ),
              },
              { header: 'Sexo', cell: (m) => SEX_TEXT[m.sex] ?? m.sex },
              { header: 'Documento', cell: (m) => <span className="font-mono">…{m.documentHint}</span> },
              {
                header: 'Acción',
                end: true,
                cell: (m) =>
                  !linkClosed && (m.role === 'PASSENGER' || m.role === 'COMPANION') ? (
                    <Button
                      variant="secondary"
                      aria-label={`Restablecer Acceso de ${m.firstNames} ${m.lastNames}`}
                      onClick={() => {
                        setNotice(undefined);
                        setResetting(m);
                      }}
                    >
                      Restablecer Acceso
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        )}
      </Loaded>
      <AddPassengerDialog
        open={adding}
        groupId={groupId}
        defaultDocumentType={defaultDocumentType}
        onClose={() => {
          setAdding(false);
        }}
        onDone={(name, username) => {
          setAdding(false);
          onDone(`${name} ya puede ingresar`, `Decile al pasajero: su usuario es «${username}». ${ONBOARDING}`);
        }}
      />
      <ResetAccessDialog
        member={resetting}
        groupId={groupId}
        onClose={() => {
          setResetting(undefined);
        }}
        onDone={(name, username) => {
          setResetting(undefined);
          onDone(
            `Acceso de ${name} restablecido`,
            `Decile al pasajero: su usuario sigue siendo «${username}». La contraseña volvió a ser su número de documento (sin puntos ni guion). Al ingresar debe elegir una clave nueva de al menos 8 caracteres.`,
          );
        }}
      />
    </section>
  );
}

export function AddPassengerDialog({
  open,
  groupId,
  defaultDocumentType,
  defaultReason = DEFAULT_REASON,
  onClose,
  onDone,
}: {
  readonly open: boolean;
  readonly groupId: string;
  readonly defaultDocumentType: string;
  /** The reason written in the form: the guide's emergency wording unless the caller (an administrator) says otherwise. */
  readonly defaultReason?: string;
  readonly onClose: () => void;
  readonly onDone: (name: string, username: string) => void;
}) {
  const empty = {
    firstNames: '',
    lastNames: '',
    type: defaultDocumentType,
    number: '',
    sex: '',
    reason: defaultReason,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const complete =
    form.firstNames.trim() !== '' &&
    form.lastNames.trim() !== '' &&
    form.number !== '' &&
    form.sex !== '' &&
    form.reason.trim().length >= 3;
  const close = (): void => {
    if (busy) return;
    setForm(empty);
    setError(undefined);
    onClose();
  };
  const save = async (): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      const created = await backoffice.guideAddPassenger(
        groupId,
        {
          firstNames: form.firstNames.trim(),
          lastNames: form.lastNames.trim(),
          sex: form.sex as Member['sex'],
          document: { type: form.type, number: form.number },
        },
        form.reason.trim(),
      );
      const name = `${form.firstNames.trim()} ${form.lastNames.trim()}`;
      setForm(empty);
      onDone(name, created.username ?? '');
    } catch (failure) {
      setError(dialogMessage(failure)); // inline, inside the dialog: the guide keeps what was typed
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      title="Agregar un pasajero"
      onClose={close}
      actions={
        <>
          <Button variant="secondary" disabled={busy} onClick={close}>
            Cancelar
          </Button>
          <Button
            loading={busy}
            disabled={!complete}
            onClick={() => {
              void save();
            }}
          >
            Agregar pasajero
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== undefined && <Alert tone="error">{error}</Alert>}
        <TextField
          label="Nombres"
          autoComplete="off"
          maxLength={100}
          hint="Todos los nombres, por ejemplo «Juan Pablo»."
          value={form.firstNames}
          onChange={(event) => {
            setForm({ ...form, firstNames: event.target.value });
          }}
        />
        <TextField
          label="Apellidos"
          autoComplete="off"
          maxLength={100}
          hint="Los dos apellidos, por ejemplo «De la Fuente Soto»."
          value={form.lastNames}
          onChange={(event) => {
            setForm({ ...form, lastNames: event.target.value });
          }}
        />
        <SelectField
          label="Tipo de documento"
          options={DOCUMENT_TYPES}
          value={form.type}
          onChange={(event) => {
            setForm({ ...form, type: event.target.value });
          }}
        />
        <TextField
          label="Número de documento"
          autoComplete="off"
          inputMode="text"
          maxLength={30}
          hint="Podés escribirlo con puntos o guion: se limpia solo."
          value={form.number}
          onChange={(event) => {
            setForm({ ...form, number: sanitizeDocument(event.target.value) });
          }}
        />
        <SelectField
          label="Sexo"
          placeholder="Elegí una opción"
          options={[
            { value: 'FEMALE', label: 'Mujer' },
            { value: 'MALE', label: 'Varón' },
            { value: 'UNDEFINED', label: 'Sin definir' },
          ]}
          value={form.sex}
          onChange={(event) => {
            setForm({ ...form, sex: event.target.value });
          }}
        />
        <TextField
          label="Motivo"
          maxLength={500}
          hint="Queda registrado en la auditoría."
          value={form.reason}
          onChange={(event) => {
            setForm({ ...form, reason: event.target.value });
          }}
        />
      </div>
    </Dialog>
  );
}

function ResetAccessDialog({
  member,
  groupId,
  onClose,
  onDone,
}: {
  readonly member: Member | undefined;
  readonly groupId: string;
  readonly onClose: () => void;
  readonly onDone: (name: string, username: string) => void;
}) {
  const [reason, setReason] = useState(DEFAULT_REASON);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const close = (): void => {
    if (busy) return;
    setError(undefined);
    onClose();
  };
  const confirm = async (): Promise<void> => {
    if (member === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await backoffice.guideResetAccess(groupId, member.id, reason.trim());
      onDone(`${member.firstNames} ${member.lastNames}`, result.username);
    } catch (failure) {
      setError(dialogMessage(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={member !== undefined}
      title="¿Restablecer el acceso?"
      onClose={close}
      actions={
        <>
          <Button variant="secondary" disabled={busy} onClick={close}>
            Cancelar
          </Button>
          <Button
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => {
              void confirm();
            }}
          >
            Restablecer Acceso
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== undefined && <Alert tone="error">{error}</Alert>}
        <p>
          {member?.firstNames} {member?.lastNames}: La contraseña se restablecerá al documento y el pasajero deberá
          elegir una clave nueva de al menos 8 caracteres al iniciar sesión.
        </p>
        <TextField
          label="Motivo"
          maxLength={500}
          hint="Queda registrado en la auditoría."
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
      </div>
    </Dialog>
  );
}
